import { useState } from "react";
import { toast } from "sonner";
import { Loader2, PackageCheck, Truck, MapPin, CheckCircle2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { FileUpload } from "../FileUpload";
import { adminApi, formatApiErrorDetail } from "../../lib/authApi";

const err = (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail));

export const DispatchPanel = ({ order, onUpdated }) => {
  const [form, setForm] = useState({ transporter: "", mode: "", tracking_ref: "", packages: "1", weight: "", note: "" });
  const [files, setFiles] = useState([]);
  const [transit, setTransit] = useState({ note: "", location: "" });
  const [deliveredNote, setDeliveredNote] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const run = async (fn, ok) => {
    setBusy(true);
    try {
      const { data } = await fn();
      onUpdated(data);
      setFiles([]);
      toast.success(ok);
    } catch (e) {
      err(e);
    } finally {
      setBusy(false);
    }
  };

  if (order.status === "QUALITY_PASSED") {
    return (
      <Button size="sm" disabled={busy} onClick={() => run(() => adminApi.post(`/admin/orders/${order.id}/dispatch/ready`, { note: "" }), "Order is ready for dispatch.")} className="rounded-full" data-testid={`admin-dispatch-ready-btn-${order.id}`}>
        {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" strokeWidth={1.5} /> : <PackageCheck className="h-4 w-4 mr-1.5" strokeWidth={1.5} />} Ready for dispatch
      </Button>
    );
  }

  if (order.status === "READY_FOR_DISPATCH") {
    const shipTo = order.delivery?.ship_to;
    return (
      <div className="w-full mt-2 p-5 rounded-lg bg-muted/40 border border-border space-y-4" data-testid={`admin-dispatch-panel-${order.id}`}>
        {!shipTo && <p className="text-sm text-amber-600 dark:text-amber-400" data-testid={`admin-dispatch-no-shipto-${order.id}`}>Waiting for the customer to enter a ship-to address before dispatch.</p>}
        <div className="grid sm:grid-cols-3 gap-3">
          <div className="space-y-1"><Label>Transporter *</Label><Input value={form.transporter} onChange={set("transporter")} data-testid={`admin-dispatch-transporter-${order.id}`} /></div>
          <div className="space-y-1"><Label>Mode</Label><Input value={form.mode} onChange={set("mode")} placeholder="Road / Air / Courier" data-testid={`admin-dispatch-mode-${order.id}`} /></div>
          <div className="space-y-1"><Label>Tracking / LR ref</Label><Input value={form.tracking_ref} onChange={set("tracking_ref")} data-testid={`admin-dispatch-tracking-${order.id}`} /></div>
          <div className="space-y-1"><Label>Packages</Label><Input type="number" min="1" value={form.packages} onChange={set("packages")} data-testid={`admin-dispatch-packages-${order.id}`} /></div>
          <div className="space-y-1"><Label>Weight</Label><Input value={form.weight} onChange={set("weight")} placeholder="14.5 kg" data-testid={`admin-dispatch-weight-${order.id}`} /></div>
          <div className="space-y-1"><Label>Note</Label><Input value={form.note} onChange={set("note")} data-testid={`admin-dispatch-note-${order.id}`} /></div>
        </div>
        <FileUpload files={files} onChange={setFiles} maxFiles={20} testId={`admin-dispatch-evidence-upload-${order.id}`} />
        <Button size="sm" disabled={busy || !shipTo || !form.transporter.trim()} onClick={() => run(() => adminApi.post(`/admin/orders/${order.id}/dispatch`, { ...form, packages: parseInt(form.packages, 10) || 1, evidence_file_ids: files.map((f) => f.id) }), "Dispatched.")} className="rounded-full" data-testid={`admin-dispatch-btn-${order.id}`}>
          {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" strokeWidth={1.5} /> : <Truck className="h-4 w-4 mr-1.5" strokeWidth={1.5} />} Dispatch
        </Button>
      </div>
    );
  }

  if (["DISPATCHED", "IN_TRANSIT"].includes(order.status)) {
    return (
      <div className="w-full mt-2 p-5 rounded-lg bg-muted/40 border border-border space-y-4" data-testid={`admin-transit-panel-${order.id}`}>
        <div className="grid sm:grid-cols-3 gap-3 items-end">
          <div className="space-y-1 sm:col-span-2"><Label>Transit update</Label><Input value={transit.note} onChange={(e) => setTransit({ ...transit, note: e.target.value })} placeholder="Left origin hub" data-testid={`admin-transit-note-${order.id}`} /></div>
          <div className="space-y-1"><Label>Location</Label><Input value={transit.location} onChange={(e) => setTransit({ ...transit, location: e.target.value })} data-testid={`admin-transit-location-${order.id}`} /></div>
        </div>
        <div className="flex flex-wrap gap-3">
          <Button size="sm" variant="outline" disabled={busy || !transit.note.trim()} onClick={() => run(() => adminApi.post(`/admin/orders/${order.id}/transit`, transit).then((r) => { setTransit({ note: "", location: "" }); return r; }), "Transit update added.")} className="rounded-full" data-testid={`admin-transit-btn-${order.id}`}>
            <MapPin className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Add transit update
          </Button>
        </div>
        <div className="pt-3 border-t border-border space-y-3">
          <div className="space-y-1"><Label>Delivery note (proof of delivery below)</Label><Input value={deliveredNote} onChange={(e) => setDeliveredNote(e.target.value)} data-testid={`admin-delivered-note-${order.id}`} /></div>
          <FileUpload files={files} onChange={setFiles} maxFiles={20} testId={`admin-delivered-evidence-upload-${order.id}`} />
          <Button size="sm" disabled={busy} onClick={() => run(() => adminApi.post(`/admin/orders/${order.id}/delivered`, { note: deliveredNote, evidence_file_ids: files.map((f) => f.id) }), "Marked delivered — awaiting customer confirmation.")} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`admin-delivered-btn-${order.id}`}>
            {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" strokeWidth={1.5} /> : <CheckCircle2 className="h-4 w-4 mr-1.5" strokeWidth={1.5} />} Mark delivered
          </Button>
        </div>
      </div>
    );
  }

  if (order.status === "DELIVERED") {
    return <p className="mt-2 text-sm text-muted-foreground" data-testid={`admin-awaiting-confirmation-${order.id}`}>Delivered — waiting for the customer to confirm receipt. Vectron does not confirm on the customer's behalf.</p>;
  }
  return null;
};
